import React, { useState, useEffect } from 'react';
import {
  X,
  Check,
  Gavel,
  Lock,
  RefreshCw,
  DollarSign,
  XCircle,
  Shield,
  Plane
} from 'lucide-react';
import PropTypes from 'prop-types';
import useDropdownPosition from '../../../hooks/useDropdownPosition';

const SentencingTypesDropdown = ({
  isOpen,
  onClose,
  selectedSentencingTypes = [],
  onSentencingTypesChange,
  buttonRef
}) => {
  const [focusedIndex, setFocusedIndex] = useState(-1);

  // Use smart positioning hook
  const { dropdownRef, position } = useDropdownPosition(isOpen, 350, buttonRef);

  // Check if we're on a narrow screen (mobile)
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 640); // sm breakpoint
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Handle click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        // Check if the click is on a filter button
        const isFilterButton = event.target.closest('[data-filter-button]');
        if (isFilterButton) {
          return;
        }
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, onClose, dropdownRef]);

  const sentencingOptions = [
    {
      id: 'confinement',
      label: 'Confinement',
      description: 'Jail or prison time',
      icon: Lock,
      color: 'bg-red-50 text-red-700 border-red-200'
    },
    {
      id: 'probation',
      label: 'Probation',
      description: 'Supervised release',
      icon: Shield,
      color: 'bg-blue-50 text-blue-700 border-blue-200'
    },
    {
      id: 'diversion',
      label: 'Diversion',
      description: 'Alternative programs',
      icon: RefreshCw,
      color: 'bg-green-50 text-green-700 border-green-200'
    },
    {
      id: 'fine',
      label: 'Fine',
      description: 'Monetary penalties',
      icon: DollarSign,
      color: 'bg-yellow-50 text-yellow-700 border-yellow-200'
    },
    {
      id: 'dismissed',
      label: 'Dismissed',
      description: 'Charges dismissed',
      icon: XCircle,
      color: 'bg-gray-50 text-gray-700 border-gray-200'
    },
    {
      id: 'fugitive extradition',
      label: 'Fugitive Extradition',
      description: 'Fugitive extradited',
      icon: Plane,
      color: 'bg-purple-50 text-purple-700 border-purple-200'
    }
  ];

  // Handle sentencing type selection
  const handleSentencingTypeSelect = (type) => {
    const isSelected = selectedSentencingTypes.includes(type);
    if (isSelected) {
      onSentencingTypesChange(
        selectedSentencingTypes.filter((t) => t !== type)
      );
    } else {
      onSentencingTypesChange([...selectedSentencingTypes, type]);
    }
  };

  // Handle keyboard navigation
  const handleKeyDown = (e) => {
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setFocusedIndex((prev) =>
          prev < sentencingOptions.length - 1 ? prev + 1 : prev
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setFocusedIndex((prev) => (prev > 0 ? prev - 1 : -1));
        break;
      case 'Enter':
        e.preventDefault();
        if (focusedIndex >= 0 && sentencingOptions[focusedIndex]) {
          handleSentencingTypeSelect(sentencingOptions[focusedIndex].id);
        }
        break;
      case 'Escape':
        onClose();
        break;
      default:
        break;
    }
  };

  // Handle clear all
  const handleClearAll = () => {
    onSentencingTypesChange([]);
  };

  if (!isOpen) return null;

  return (
    <>
      {/* Mobile backdrop */}
      {isMobile && (
        <div
          className="fixed inset-0 bg-black bg-opacity-25 z-40"
          onClick={onClose}
        />
      )}

      <div
        ref={dropdownRef}
        className={`${
          isMobile
            ? 'fixed inset-x-4 top-1/2 -translate-y-1/2 w-auto max-w-sm mx-auto'
            : 'absolute top-full mt-1 w-[350px] sm:w-[350px]'
        } bg-white border border-gray-200 rounded-lg shadow-lg z-50`}
        style={
          isMobile
            ? {
                maxWidth: 'calc(100vw - 32px)',
                minWidth: '280px'
              }
            : {
                left: position.left,
                right: position.right,
                maxWidth: 'calc(100vw - 32px)', // Ensure dropdown never exceeds viewport width
                minWidth: '350px' // Ensure minimum usable width
              }
        }
      >
        <div className="p-4">
          {/* Header */}
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <Gavel className="w-4 h-4 text-gray-700" />
              <h3 className="text-sm font-medium text-gray-900">
                Select Sentencing Types
              </h3>
            </div>
            <button
              onClick={onClose}
              className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-md transition-colors"
              title="Close"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Selected Sentencing Types */}
          {selectedSentencingTypes.length > 0 && (
            <div className="mb-4">
              <div className="text-xs font-medium text-gray-700 mb-2">
                Selected ({selectedSentencingTypes.length})
              </div>
              <div className="flex flex-wrap gap-1">
                {selectedSentencingTypes.map((type) => {
                  const option = sentencingOptions.find(
                    (opt) => opt.id === type
                  );
                  return (
                    <span
                      key={type}
                      className="inline-flex items-center px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded-full"
                    >
                      {option?.label || type}
                      <button
                        onClick={() => handleSentencingTypeSelect(type)}
                        className="ml-1 hover:bg-blue-200 rounded-full p-0.5"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </span>
                  );
                })}
              </div>
            </div>
          )}

          {/* Sentencing Types List */}
          <div className="max-h-64 overflow-y-auto p-2">
            <div className="space-y-2">
              {sentencingOptions.map((option, index) => {
                const isSelected = selectedSentencingTypes.includes(option.id);
                const isFocused = focusedIndex === index;
                const Icon = option.icon;

                return (
                  <button
                    key={option.id}
                    onClick={() => handleSentencingTypeSelect(option.id)}
                    onKeyDown={handleKeyDown}
                    className={`w-full p-3 text-left text-sm border rounded-md transition-colors box-border ${
                      isSelected
                        ? 'bg-blue-50 border-blue-200 text-blue-900'
                        : isFocused
                          ? 'bg-gray-50 border-gray-300'
                          : 'bg-white border-gray-200 hover:bg-gray-50'
                    }`}
                    onMouseEnter={() => setFocusedIndex(index)}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <Icon
                          className={`w-4 h-4 ${
                            isSelected ? 'text-blue-600' : 'text-gray-400'
                          }`}
                        />
                        <div>
                          <div className="font-medium">{option.label}</div>
                          <div className="text-xs text-gray-500 mt-0.5">
                            {option.description}
                          </div>
                        </div>
                      </div>
                      {isSelected && (
                        <Check className="w-4 h-4 text-blue-600" />
                      )}
                    </div>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Actions */}
          <div className="flex items-center justify-between pt-3 border-t border-gray-200 mt-4">
            <button
              onClick={handleClearAll}
              className="text-sm text-gray-500 hover:text-gray-700"
              disabled={selectedSentencingTypes.length === 0}
            >
              Clear All
            </button>
            <button
              onClick={onClose}
              className="px-3 py-1.5 text-sm text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
            >
              Done
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

SentencingTypesDropdown.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  selectedSentencingTypes: PropTypes.arrayOf(PropTypes.string),
  onSentencingTypesChange: PropTypes.func.isRequired,
  buttonRef: PropTypes.object
};

export default SentencingTypesDropdown;
